import { useState } from "react";
import { Plus, Edit3, Trash2, Eye, EyeOff } from "lucide-react";
import { useStore } from "@/context/StoreContext";
import { formatPrice } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { ComboForm } from "./ComboForm";
import { toast } from "sonner";

export function AdminCombos() {
  const { combos, addCombo, updateCombo, deleteCombo } = useStore();
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const openNewForm = () => {
    setEditing(null);
    setShowForm(true);
  };

  const openEditForm = (combo) => {
    setEditing(combo);
    setShowForm(true);
  };

  const saveCombo = async (payload) => {
    setBusyId("saving");
    try {
      if (editing) {
        await updateCombo(editing._id || editing.id, payload, "Combo updated successfully ✨");
      } else {
        await addCombo(payload, "New combo added! 🎉");
      }
      setEditing(null);
      setShowForm(false);
    } catch (err) {
      // Error handled in store context
    } finally {
      setBusyId(null);
    }
  };

  const toggleActive = async (combo) => {
    const id = combo._id || combo.id;
    const next = !combo.isActive;
    setBusyId(id);
    try {
      await updateCombo(id, { isActive: next }, `Combo is now ${next ? 'Active 🟢' : 'Hidden 👁️'}`);
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (combo) => {
    if (!window.confirm(`Are you sure you want to delete ${combo.name}?`)) return;
    setBusyId(combo._id || combo.id);
    try {
      await deleteCombo(combo._id || combo.id, "Combo deleted!");
    } finally {
      setBusyId(null);
    }
  };

  if (showForm) {
    return (
      <div className="mx-auto max-w-5xl">
        <ComboForm 
          combo={editing} 
          onSubmit={saveCombo} 
          onCancel={() => { setShowForm(false); setEditing(null); }} 
        />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="font-display text-2xl font-semibold text-[#0f172a]">
            Combos & Bundles
          </h2>
          <p className="mt-1 text-sm text-[#64748b]">
            Manage bundled product offerings.
          </p>
        </div>
        <Button onClick={openNewForm} className="bg-[#0f172a] text-white hover:bg-black">
          <Plus size={16} className="mr-2" />
          Create Combo
        </Button>
      </div>

      <div className="rounded-[20px] border border-[#e2e8f0] bg-white overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm text-[#334155]">
            <thead className="bg-[#f8fafc] text-xs uppercase tracking-wider text-[#64748b]">
              <tr>
                <th className="px-6 py-4 font-semibold">Combo Details</th>
                <th className="px-6 py-4 font-semibold">Price / MRP</th>
                <th className="px-6 py-4 font-semibold">Status</th>
                <th className="px-6 py-4 font-semibold text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#e2e8f0]">
              {combos.length === 0 ? (
                <tr>
                  <td colSpan="4" className="px-6 py-12 text-center text-[#64748b]">
                    No combos found. Create one to get started!
                  </td>
                </tr>
              ) : (
                combos.map((combo) => {
                  const id = combo._id || combo.id;
                  const isBusy = busyId === id;
                  return (
                    <tr key={id} className={`transition-colors hover:bg-[#f8fafc] ${isBusy ? "opacity-50" : ""}`}>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-4">
                          <div className="h-12 w-12 shrink-0 rounded-[10px] border border-[#e2e8f0] bg-[#f8fafc] overflow-hidden">
                            {combo.image ? (
                              <img src={combo.image} alt={combo.name} className="h-full w-full object-cover" />
                            ) : (
                              <div className="grid h-full place-items-center text-xs text-gray-400">No Img</div>
                            )}
                          </div>
                          <div>
                            <p className="font-semibold text-[#0f172a]">{combo.name}</p>
                            <p className="text-xs text-[#64748b] mt-0.5">{combo.tagline}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="font-semibold text-[#0f172a]">{formatPrice(combo.price)}</div>
                        <div className="text-xs text-[#94a3b8] line-through mt-0.5">{formatPrice(combo.original_price)}</div>
                      </td>
                      <td className="px-6 py-4">
                        <button
                          onClick={() => toggleActive(combo)}
                          disabled={isBusy}
                          className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold transition ${
                            combo.isActive !== false
                              ? "bg-green-100 text-green-700 hover:bg-green-200"
                              : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                          }`}
                        >
                          {combo.isActive !== false ? <Eye size={12} /> : <EyeOff size={12} />}
                          {combo.isActive !== false ? "Live" : "Hidden"}
                        </button>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => openEditForm(combo)}
                            disabled={isBusy}
                            className="p-2 text-[#64748b] transition hover:text-[#0f172a] hover:bg-gray-100 rounded-lg"
                            title="Edit Combo"
                          >
                            <Edit3 size={16} />
                          </button>
                          <button
                            onClick={() => handleDelete(combo)}
                            disabled={isBusy}
                            className="p-2 text-red-400 transition hover:text-red-600 hover:bg-red-50 rounded-lg"
                            title="Delete Combo"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
